// src/components/artists/ArtistKeyMetrics.tsx
import React from 'react';
import {
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  UsersIcon,
  UserIcon,
  MusicalNoteIcon,
  HeartIcon
} from '@heroicons/react/24/outline';

interface Artist {
  id: number;
  name: string;
  genre: string;
  score: number;
  previousScore: number;
  followers: number;
  monthlyListeners: number;
  totalStreams: number;
  saveRate: number;
}

interface ProcessedMetric {
  month: string;
  streams: number;
  listeners: number;
  saves: number;
  followers: number;
  playlists: number;
  score: number;
  saveRate: number;
  streamsChange?: number | null;
  listenersChange?: number | null;
  savesChange?: number | null;
  playlistsChange?: number | null;
  scoreChange?: number | null;
}

interface ArtistKeyMetricsProps {
  artist: Artist;
  latestMetric: ProcessedMetric | null;
  scoreChange: number;
  isScorePositive: boolean;
}

export const ArtistKeyMetrics: React.FC<ArtistKeyMetricsProps> = ({ 
  artist, 
  latestMetric, 
  scoreChange,
  isScorePositive
}) => {
  // Calculate save rate
  const saveRate = latestMetric && latestMetric.streams > 0
    ? ((latestMetric.saves / latestMetric.streams) * 100).toFixed(2)
    : typeof artist.saveRate === 'number' 
      ? artist.saveRate.toFixed(2)
      : '0.00';

  return (
    <div className="bg-white rounded-lg shadow p-6 h-full flex flex-col">
      <h2 className="text-lg font-semibold text-gray-700 mb-4">Key Metrics</h2>
      <div className="space-y-3 flex-grow">
        {/* Score */}
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="p-2 bg-green-50 rounded-lg mr-3">
              <ChartBarIcon className="h-5 w-5 text-green-600" />
            </div>
            <span className="text-gray-600">Score</span>
          </div>
          <span className="font-medium text-gray-900">
            {latestMetric ? latestMetric.score.toFixed(2) : artist.score.toFixed(2)}
          </span>
        </div>
        
        {/* Score Change */}
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="p-2 bg-green-50 rounded-lg mr-3">
              {isScorePositive ? (
                <ArrowTrendingUpIcon className="h-5 w-5 text-green-600" />
              ) : (
                <ArrowTrendingDownIcon className="h-5 w-5 text-red-500" />
              )}
            </div>
            <span className="text-gray-600">Score Change</span>
          </div>
          <span className={`font-medium ${isScorePositive ? 'text-green-600' : 'text-red-600'}`}>
            {isScorePositive ? '+' : ''}{scoreChange.toFixed(2)}
          </span>
        </div>
        
        {/* Followers */}
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="p-2 bg-indigo-50 rounded-lg mr-3">
              <UsersIcon className="h-5 w-5 text-indigo-600" />
            </div>
            <span className="text-gray-600">Followers</span>
          </div>
          <span className="font-medium text-gray-900">
            {latestMetric && latestMetric.followers 
              ? latestMetric.followers.toLocaleString() 
              : artist.followers.toLocaleString()}
          </span>
        </div>
        
        {/* Monthly Listeners */}
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="p-2 bg-blue-50 rounded-lg mr-3">
              <UserIcon className="h-5 w-5 text-blue-600" />
            </div>
            <span className="text-gray-600">Monthly Listeners</span>
          </div>
          <span className="font-medium text-gray-900">
            {latestMetric 
              ? latestMetric.listeners.toLocaleString() 
              : artist.monthlyListeners.toLocaleString()}
          </span>
        </div>
        
        {/* Monthly Streams */}
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="p-2 bg-purple-50 rounded-lg mr-3">
              <MusicalNoteIcon className="h-5 w-5 text-purple-600" />
            </div>
            <span className="text-gray-600">Monthly Streams</span>
          </div>
          <span className="font-medium text-gray-900">
            {latestMetric 
              ? latestMetric.streams.toLocaleString() 
              : artist.totalStreams.toLocaleString()}
          </span>
        </div>
        
        {/* Save Rate */}
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="p-2 bg-pink-50 rounded-lg mr-3">
              <HeartIcon className="h-5 w-5 text-pink-600" />
            </div>
            <span className="text-gray-600">Save Rate</span>
          </div>
          <span className="font-medium text-gray-900">{saveRate}%</span>
        </div>
      </div>
    </div>
  );
};